import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  Skeleton,
  Typography,
  Tooltip,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/AuthService';
import { getReportsByCase, ReportData } from '../../../services/reportAPI';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha } from '@mui/material/styles';

interface FinalizedReportsLinksProps {
  caseId: string;
  currentReportId?: string; // To highlight current report if viewing one
  onReportClick?: (reportId: string) => void; // Optional callback for viewing report inline
}

const FinalizedReportsLinks: React.FC<FinalizedReportsLinksProps> = ({
  caseId,
  currentReportId,
  onReportClick,
}) => {
  const [reports, setReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const authService = useMemo(() => new AuthService(), []);

  const fetchFinalizedReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await authService.getAccessToken();
      if (!token) throw new Error('No access token available');

      // Fetch reports for this case
      const fetchedReports = await getReportsByCase(token, caseId, 100, 0);
      
      // Filter to only show finalized/confirmed reports and reports with valid IDs
      const finalizedReports = fetchedReports.filter(
        (report) => report.id && (report.isConfirmed || report.status === 'finalized' || report.status === 'final')
      );
      
      setReports(finalizedReports);
    } catch (err) {
      console.error('Failed to fetch finalized reports:', err);
      setError('Failed to load finalized reports');
    } finally {
      setLoading(false);
    }
  }, [caseId, authService]);

  useEffect(() => {
    fetchFinalizedReports();
  }, [fetchFinalizedReports]);

  const handleReportClick = (reportId: string, patientId?: string) => {
    // If onReportClick callback provided, use it (for inline viewing)
    if (onReportClick) {
      onReportClick(reportId);
    } else {
      // Otherwise, navigate to the report preview page with caseId and patientId as query params
      const queryParams = new URLSearchParams();
      queryParams.set('caseId', caseId);
      if (patientId) {
        queryParams.set('patientId', patientId);
      }
      navigate(`/report-preview/${reportId}?${queryParams.toString()}`);
    }
  };

  const handleNavigateToReport = (reportId: string, patientId: string | undefined, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent chip click
    const queryParams = new URLSearchParams();
    queryParams.set('caseId', caseId);
    queryParams.set('edit', 'true');
    if (patientId) {
      queryParams.set('patientId', patientId);
    }
    navigate(`/report-preview/${reportId}?${queryParams.toString()}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string | undefined, isConfirmed: boolean | undefined): "success" | "warning" | "default" => {
    if (isConfirmed) return "success";
    if (status === 'finalized' || status === 'final') return "success";
    if (status === 'review') return "warning";
    return "default";
  };

  // Don't render anything if no reports exist
  if (!loading && reports.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
        borderRadius: 2,
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      {/* Header with expand/collapse */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: expanded ? 1.5 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600} color="primary">
            Finalized Reports for this Case
          </Typography>
          {!loading && (
            <Chip
              label={reports.length}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ ml: 1 }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={140}
                height={32}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {reports.map((report) => {
              // Skip reports without IDs
              if (!report.id) return null;
              
              const isCurrentReport = report.id === currentReportId;
              const statusColor = getStatusColor(report.status, report.isConfirmed);
              
              return (
                <Box
                  key={report.id}
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2">
                          Report ID: {report.id}
                        </Typography>
                        {report.versionNumber !== undefined && (
                          <Typography variant="body2">
                            Version: {report.versionNumber}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          Patient: {report.patientId || 'Not assigned'}
                        </Typography>
                        <Typography variant="body2">
                          Created: {report.createdAt ? formatDate(report.createdAt) : 'Unknown'}
                        </Typography>
                        <Typography variant="body2">
                          Status: {report.status || 'draft'}
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Chip
                      icon={
                        report.isConfirmed ? (
                          <CheckCircleIcon />
                        ) : (
                          <EditIcon />
                        )
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption">
                            {report.versionNumber !== undefined && report.versionNumber !== null ? `v${report.versionNumber}` : 'Report'}
                          </Typography>
                          {report.patientId && (
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              • {report.patientId.substring(0, 4)}...
                            </Typography>
                          )}
                        </Box>
                      }
                      color={statusColor}
                      variant={isCurrentReport ? "filled" : "outlined"}
                      onClick={() => handleReportClick(report.id!, report.patientId)}
                      size="small"
                      sx={{
                        cursor: 'pointer',
                        ...(isCurrentReport && {
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                        }),
                      }}
                    />
                  </Tooltip>
                  
                  {/* View Button */}
                  <Tooltip title="View Report">
                    <IconButton
                      size="small"
                      onClick={(e) => handleNavigateToReport(report.id!, report.patientId, e)}
                      sx={{
                        width: 28,
                        height: 28,
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default FinalizedReportsLinks;